import DropdownInput from 'components/Dropdown/Dropdown';
import Fields from 'components/Fields';
import { Field, FieldArray } from 'formik';
import React, { useRef, useState } from 'react';
import CloseIcon from '@material-ui/icons/Close';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { Notify, unFormatPercent } from 'utils';
import ErrorIcon from '@material-ui/icons/ErrorOutline';
import { Tooltip } from '@material-ui/core';
import { secondaryText } from 'utils/colors';
import { ContentCopy } from '@mui/icons-material';
import FloatingModal from 'components/Base/Modal/FloatingModal/FloatingModal';
import { Button, Prompt } from 'components/Base';
import EVLogo from 'assets/images/EV_Logo_2021.png';

type CustomAllocationsGroupProps = {
  timeHorizon: any;
  allocations: any[];
  assetClasses: any;
  riskLevel: number;
  setFieldValue: Function;
  values: any;
  errors?: {
    asset_class?: string;
    percentage?: string;
  };
  isDefault: boolean;
  defaults: any;
};

const CustomAllocationsGroup = ({
  timeHorizon,
  allocations,
  assetClasses,
  riskLevel,
  setFieldValue,
  values,
  errors,
  isDefault,
  defaults
}: CustomAllocationsGroupProps) => {
  const [isCopyModalVisible, setIsCopyModalVisible] = useState(false);
  const [isResetModalVisible, setIsResetModalVisible] = useState(false);
  const [isCopyPromptVisible, setIsCopyPromptVisible] = useState(false);
  const [copiedValues, setCopiedValues] = useState({});
  const iconContainerRef = useRef(null);

  const baseName = `allocations.${riskLevel}.${timeHorizon.value}`;

  return (
    <div
      style={{
        backgroundColor: '#fff',
        borderRadius: '25px',
        padding: '0.5rem 0.5rem 3.5rem 0.5rem',
        maxWidth: '300px',
        minWidth: '275px',
        flex: 1,
        position: 'relative'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', padding: '.5rem' }}>
        <div style={{ fontWeight: 600 }}>{timeHorizon.name} years</div>
        {isDefault && (
          <img style={{ height: '20px', marginLeft: '0.5rem' }} src={EVLogo} />
        )}
        <div
          style={{ marginLeft: 'auto', position: 'relative' }}
          ref={iconContainerRef}
        >
          <ContentCopy
            fontSize="small"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setIsCopyModalVisible(!isCopyModalVisible);
            }}
            style={{
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e: any) => (e.currentTarget.style.color = 'grey')}
            onMouseLeave={(e: any) => (e.currentTarget.style.color = 'black')}
          />
          {isCopyModalVisible && (
            <FloatingModal
              buttonRef={iconContainerRef}
              onClose={() => setIsCopyModalVisible(false)}
              adjust={false}
            >
              Copy from:
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                  marginTop: '0.5rem',
                  width: '250px'
                }}
              >
                {values.timeHorizons
                  .filter(
                    (otherTimeHorizon: any) =>
                      otherTimeHorizon.value !== timeHorizon.value
                  )
                  .map((otherTimeHorizon: any) => (
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      key={otherTimeHorizon.value}
                      onClick={e => {
                        e.stopPropagation();
                        setCopiedValues(values.form[otherTimeHorizon.value]);
                        setIsCopyPromptVisible(true);
                        setIsCopyModalVisible(false);
                      }}
                    >
                      {otherTimeHorizon.name} years
                    </Button>
                  ))}
              </div>
            </FloatingModal>
          )}
        </div>
      </div>
      <FieldArray name={baseName}>
        {arrayHelpers => (
          <>
            {allocations.map((allocation: any, index: number) => {
              const itemName = `${baseName}[${index}]`;
              return (
                <div
                  style={{ display: 'flex', alignItems: 'end', gap: '4px' }}
                  key={index}
                >
                  <DropdownInput
                    options={assetClasses}
                    showClear={false}
                    value={
                      assetClasses.find(
                        (assetClass: any) =>
                          assetClass.value == allocation?.asset_class
                      )?.name || ''
                    }
                    label=""
                    onChange={(assetClass: any) => {
                      setFieldValue(
                        `${itemName}.asset_class`,
                        assetClass.value
                      );
                    }}
                    objectKey={'name'}
                    search
                  />
                  <Field
                    name={`${itemName}.percentage`}
                    placeholder="0.00%"
                    component={Fields.v2.Percent}
                    style={{ width: '75px' }}
                  />
                  <div>
                    <CloseIcon
                      onClick={() => arrayHelpers.remove(index)}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                </div>
              );
            })}
            {errors?.asset_class && (
              <div
                style={{
                  display: 'flex',
                  fontSize: '12px',
                  color: secondaryText,
                  alignItems: 'center',
                  gap: '4px',
                  marginTop: '0.25rem'
                }}
              >
                <ErrorIcon color="error" /> {errors?.asset_class}
              </div>
            )}
            <div
              style={{
                marginTop: '0.5rem',
                cursor: 'pointer',
                display: 'inline-block'
              }}
              onClick={() => arrayHelpers.push({})}
            >
              <AddCircleIcon color="secondary" />
            </div>
            {!isDefault && (
              <Button
                variant="contained"
                color="primary"
                style={{
                  position: 'absolute',
                  bottom: '0.5rem',
                  left: '0.5rem'
                }}
                onClick={() => setIsResetModalVisible(true)}
              >
                Reset
              </Button>
            )}
            {allocations.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '0.5rem',
                  right: '2rem',
                  fontWeight: 600
                }}
              >
                {Math.round(
                  allocations.reduce(
                    (acc, cur) => acc + Number(unFormatPercent(cur.percentage)),
                    0
                  ) * 100
                ) / 100}
                %
              </div>
            )}
            {errors?.percentage && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '0rem',
                  right: '0.25rem'
                }}
              >
                <Tooltip title={errors?.percentage} arrow>
                  <ErrorIcon color="error" />
                </Tooltip>
              </div>
            )}
            <Prompt
              isOpen={isResetModalVisible}
              title={`Are you sure you want to reset typical asset allocations to their default settings?`}
              confirmAction={() => {
                setFieldValue(baseName, defaults);
                setIsResetModalVisible(false);
              }}
              cancelAction={() => {
                setIsResetModalVisible(false);
              }}
              isProcessing={false}
            />
            <Prompt
              isOpen={isCopyPromptVisible}
              title={`Are you sure you want to copy these asset allocations? This will replace all current allocations in this time horizon.`}
              confirmAction={() => {
                setFieldValue(baseName, copiedValues);
                Notify.success(`Asset allocations copied successfully.`);
                setIsCopyPromptVisible(false);
              }}
              cancelAction={() => {
                setIsCopyPromptVisible(false);
              }}
              isProcessing={false}
            />
          </>
        )}
      </FieldArray>
    </div>
  );
};

export default CustomAllocationsGroup;
